import React, { FunctionComponent, useState } from 'react'
import axios from 'axios'
import { GetDataCenterRegionString } from '../../env'
import { Features } from '../../features'
import { useMountEffect } from '../../hooks/MountEffect'
import { EmailValidator } from '../../service/EmailValidator'
import FeatureToggle from '../../toggle'
import CustomModal, { CustomModalWidth } from '../CustomModal/CustomModal'
import { ExclaimIcon } from '../Icons/Icons'
import { CodeValidationResponse } from '../ServiceProviders/Data'
import Toaster from '../Toaster'

interface Props {
  teamID: string
  onClose: () => void
}

const ServiceProviderInviteModal: FunctionComponent<Props> = props => {
  const [teamName, setTeamName] = useState('')
  const [serviceProviderCode, setServiceProviderCode] = useState('')
  const [adminEmailAddress, setAdminEmailAddress] = useState('')
  const [requesterEmailAddress, setRequesterEmailAddress] = useState('')
  const [errMessages, setErrMessages] = useState<string[]>([])
  const [sending, setSending] = useState(false)
  const [skipServiceProviderValidationEnabled, setSkipServiceProviderValidationEnabled] = useState(false)
  const [confirmInvalidCode, setConfirmInvalidCode] = useState(false)
  const [showConfirmInvalidCode, setShowConfirmInvalidCode] = useState(false)

  useMountEffect(() => {
    FeatureToggle.isOn(props.teamID, Features.SkipServiceProviderValidation).then(enabled => {
      setSkipServiceProviderValidationEnabled(enabled)
    })
  })

  const validate = async () => {
    let errs: string[] = []
    if (serviceProviderCode) {
      await axios
        .get<CodeValidationResponse>(`/api/teams/${props.teamID}/service-provider-code/${serviceProviderCode}/validate`)
        .then(resp => {
          if (!resp.data.valid) {
            errs.push('Service provider code is invalid.')
          }
        })
        .catch(() => {
          if (!skipServiceProviderValidationEnabled) {
            errs.push('Service provider code is invalid.')
          } else if (!confirmInvalidCode) {
            setShowConfirmInvalidCode(true)
            errs.push('Service provider code is invalid. Fix or confirm the invalid code to continue.')
          }
        })
    } else {
      errs.push('Service provider code is required.')
    }

    if (!requesterEmailAddress) {
      errs.push('Requester email address is required.')
    } else if (!EmailValidator.validate(requesterEmailAddress.trim())) {
      errs.push('Requester email address is invalid.')
    }

    if (!adminEmailAddress) {
      errs.push('Service provider admin email address is required.')
    } else if (!EmailValidator.validate(adminEmailAddress.trim())) {
      errs.push('Service provider admin email address is invalid.')
    }

    return errs
  }

  const inviteServiceProvider = (invite: { name: string; code: string; adminEmail: string; requesterEmail: string }) =>
    axios.post(`/api/teams/${props.teamID}/service-providers`, invite)

  const onCancelBtnClicked = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    props.onClose()
  }

  const onSendEmailBtnClicked = async (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    const errs = await validate()
    setErrMessages(errs)
    if (errs && errs.length > 0) {
      return
    }
    setSending(true)
    inviteServiceProvider({
      name: teamName,
      code: serviceProviderCode,
      adminEmail: adminEmailAddress.trim(),
      requesterEmail: requesterEmailAddress.trim(),
    })
      .then(() => {
        Toaster.notifySuccess(`${teamName} invited.`)
        setTeamName('')
        setServiceProviderCode('')
        setAdminEmailAddress('')
        setRequesterEmailAddress('')
        setErrMessages([])
        setShowConfirmInvalidCode(false)
        setConfirmInvalidCode(false)
      })
      .catch(() => Toaster.notifyFailure(`Unable to invite ${teamName}.`))
      .finally(() => setSending(false))
  }

  return (
    <CustomModal id='popup-serviceprovider' close={props.onClose} width={CustomModalWidth.w640}>
      <header className='center'>
        <h3>Invite service provider</h3>

        <p>
          Complete this form to create a service provider account in the <strong>{GetDataCenterRegionString()}</strong>{' '}
          data center.
        </p>
      </header>

      {errMessages.length > 0 && (
        <div className='alert error'>
          <i className='icon-exclaim'>
            <ExclaimIcon />
          </i>
          <div className='message'>
            <ul>
              {errMessages.map(msg => (
                <li key={msg}>{msg}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className='content'>
        <div className='field'>
          <label id='requester-email-label'>Requester email address</label>
          <input
            type='text'
            placeholder='requester@example.com'
            value={requesterEmailAddress}
            onChange={e => setRequesterEmailAddress(e.target.value)}
            aria-labelledby='requester-email-label'
          />
        </div>

        <div className='field'>
          <label id='service-provider-code-label'>Service provider code</label>
          <input
            type='text'
            placeholder='SERVICE-PROVIDER-CODE'
            value={serviceProviderCode}
            onChange={e => setServiceProviderCode(e.target.value)}
            aria-labelledby='service-provider-code-label'
          />
        </div>

        <div className='field'>
          <label id='team-name-label'>Team name</label>
          <input
            type='text'
            value={teamName}
            onChange={e => setTeamName(e.target.value)}
            aria-labelledby='team-name-label'
          />
        </div>

        <div className='field'>
          <label id='admin-email-label'>Admin email address</label>
          <input
            type='text'
            placeholder='service-provider-admin@example.net'
            value={adminEmailAddress}
            onChange={e => setAdminEmailAddress(e.target.value)}
            aria-labelledby='admin-email-label'
          />
        </div>

        {showConfirmInvalidCode && (
          <div className='field inl'>
            <label id='confirm-invalid-code-label'>
              <input type='checkbox' onChange={() => setConfirmInvalidCode(!confirmInvalidCode)} />
              Yes, I want to proceed with an invalid code.
            </label>
          </div>
        )}
      </div>

      <footer>
        <button id='cancel' className='btn outline' onClick={onCancelBtnClicked}>
          Cancel
        </button>

        <button
          id='send'
          className={`btn ${sending ? 'disabled' : ''}`}
          disabled={sending}
          onClick={onSendEmailBtnClicked}
        >
          Send email
        </button>
      </footer>
    </CustomModal>
  )
}

export default ServiceProviderInviteModal
